/**
 * Archive one unmatched demand entry with the reason it no longer needs attention. A narrow Modal rather than a
 * ConfirmDialog because it needs an answer: Archive stays disabled until a reason is picked. A refusal keeps the
 * dialog open with the server's message.
 */

import { useState } from 'react';

import { useArchiveDemand } from '@/api/queries';
import type { DemandArchiveReason, UnmatchedDemandItem } from '@/api/types';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { ARCHIVE_REASON_LABELS, ARCHIVE_REASONS } from './demandUi';

export function ArchiveDemandDialog({ item, onClose }: { item: UnmatchedDemandItem; onClose: () => void }) {
  const [reason, setReason] = useState<DemandArchiveReason | null>(null);
  const archive = useArchiveDemand();
  return (
    <Modal
      title="Archive this demand"
      subtitle={item.summary || undefined}
      size="narrow"
      onClose={onClose}
      onSubmit={() => {
        if (reason) archive.mutate({ id: item.id, reason }, { onSuccess: onClose });
      }}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <ActionButton state={buttonState(archive)} label="Archive" pendingLabel="Archiving…" disabled={!reason} />
        </>
      }
    >
      <div className="stack-md">
        <FormError error={archive.error} />
        <fieldset className="field">
          <legend className="label">Why archive it?</legend>
          {ARCHIVE_REASONS.map((value) => (
            <label key={value} className="check">
              <input type="radio" name="demand-archive-reason" value={value} required checked={reason === value}
                onChange={() => setReason(value)} />
              {ARCHIVE_REASON_LABELS[value]}
            </label>
          ))}
        </fieldset>
      </div>
    </Modal>
  );
}
